/**
 * Step-2 AI actions shared by the Search build screens: keyword ideas for an
 * ad group and headlines/descriptions for one ad, both grounded in the ad's
 * landing page (or the interview's website) and the group's keywords.
 */
import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import toast from 'react-hot-toast';
import { googleAdsService, SearchObjectiveKey } from '@/services/google-ads.service';
import { alreadyToasted, errMsg } from '@/components/google-ads/youtube/youtubeShared';
import { isHttpUrl, uid } from '@/components/google-ads/objectives/objectiveShared';
import { RsaDraft, SearchGroupDraft } from './searchShared';

const filled = (xs: string[]) => xs.map((x) => x.trim()).filter(Boolean);
const pad = (xs: string[], min: number) => (xs.length >= min ? xs : [...xs, ...Array(min - xs.length).fill('')]);

export function useSearchAi(opts: {
  objective: SearchObjectiveKey;
  setGroups: Dispatch<SetStateAction<SearchGroupDraft[]>>;
  /** Interview session, when the campaign came from "Start with AI". */
  sessionId?: string;
  /** Fallback landing page (the campaign's website). */
  siteUrl: string;
  language?: string;
  places?: string[];
  /** Headlines the group's published ads already use — a new ad should take another angle. */
  avoidLines?: string[];
}) {
  const { objective, setGroups, sessionId, siteUrl, language, places, avoidLines } = opts;
  const [busy, setBusy] = useState<string | null>(null);
  const [aiAds, setAiAds] = useState<Set<string>>(() => new Set());

  const suggest = useCallback(async (g: SearchGroupDraft) => {
    setBusy(`kw:${g.id}`);
    try {
      const r = await googleAdsService.searchAi.keywords({
        sessionId, objective,
        websiteUrl: g.ads.find((a) => isHttpUrl(a.finalUrl))?.finalUrl || siteUrl || undefined,
        groupName: g.name.trim() || undefined, existing: g.keywords.map((k) => k.text),
        places: places?.slice(0, 5), language,
      });
      setGroups((gs) => gs.map((x) => {
        if (x.id !== g.id) return x;
        const have = new Set(x.keywords.map((k) => `${k.matchType}:${k.text.toLowerCase()}`));
        const haveNeg = new Set(x.negatives.map((k) => k.text.toLowerCase()));
        return {
          ...x,
          keywords: [...x.keywords, ...r.keywords.filter((k) => !have.has(`${k.matchType}:${k.text.toLowerCase()}`)).map((k) => ({ id: uid(), ...k }))],
          negatives: [...x.negatives, ...r.negatives.filter((t) => !haveNeg.has(t.toLowerCase())).map((t) => ({ id: uid(), text: t, matchType: 'PHRASE' as const }))],
        };
      }));
      toast.success(`${r.keywords.length} keyword ideas added — remove any that don't fit.`);
    } catch (e) {
      if (!alreadyToasted(e)) toast.error(errMsg(e, 'Could not suggest keywords.'));
    } finally { setBusy(null); }
  }, [objective, setGroups, sessionId, siteUrl, language, places]);

  const writeOne = useCallback(async (g: SearchGroupDraft, a: RsaDraft): Promise<boolean> => {
    try {
      const rewrite = filled(a.headlines).length >= 3;
      // Sibling ads count too — otherwise every ad in a group opens with the same headline.
      const existing = Array.from(new Set([
        ...(rewrite ? [...filled(a.headlines), ...filled(a.descriptions)] : []),
        ...g.ads.filter((y) => y.id !== a.id).flatMap((y) => filled(y.headlines)),
        ...(avoidLines ?? []),
      ])).slice(0, 60);
      const r = await googleAdsService.searchAi.copy({
        sessionId, objective,
        websiteUrl: isHttpUrl(a.finalUrl) ? a.finalUrl : siteUrl || undefined,
        groupName: g.name.trim() || undefined, keywords: g.keywords.map((k) => k.text).slice(0, 30),
        existing: existing.length ? existing : undefined, language,
      });
      setGroups((gs) => gs.map((x) => (x.id !== g.id ? x : {
        ...x,
        ads: x.ads.map((y) => (y.id !== a.id ? y : {
          ...y, headlines: pad(r.headlines, 3), descriptions: pad(r.descriptions, 2),
          path1: y.path1 || r.path1, path2: y.path1 ? y.path2 : r.path2, finalUrl: y.finalUrl || siteUrl,
        })),
      })));
      setAiAds((prev) => new Set(prev).add(a.id));
      return true;
    } catch (e) {
      if (!alreadyToasted(e)) toast.error(errMsg(e, 'Could not write the ad.'));
      return false;
    }
  }, [objective, setGroups, sessionId, siteUrl, language, avoidLines]);

  const write = useCallback(async (g: SearchGroupDraft, a: RsaDraft) => {
    setBusy(`ad:${a.id}`);
    try { if (await writeOne(g, a)) toast.success('Ad written — edit any line before publishing.'); }
    finally { setBusy(null); }
  }, [writeOne]);

  /** Every ad that has fewer than 3 headlines, one after another. */
  const writeAll = useCallback(async (items: Array<{ g: SearchGroupDraft; a: RsaDraft }>) => {
    setBusy('all');
    let done = 0;
    try { for (const { g, a } of items) { if (await writeOne(g, a)) done++; } }
    finally { setBusy(null); }
    if (done) toast.success(`${done} ad${done === 1 ? '' : 's'} written — review every line before publishing.`);
  }, [writeOne]);

  const copyEdited = useCallback((adId: string) => setAiAds((prev) => {
    if (!prev.has(adId)) return prev;
    const n = new Set(prev); n.delete(adId); return n;
  }), []);

  return { busy, aiAds, setAiAds, suggest, write, writeAll, copyEdited };
}

export const emptyAdsOf = (groups: SearchGroupDraft[], made: (adId: string) => boolean) =>
  groups.flatMap((g) => g.ads.filter((a) => filled(a.headlines).length < 3 && !made(a.id)).map((a) => ({ g, a })));
